import React, { useEffect } from "react";
import { Card, Row, Col, Typography, Spin, message, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  InboxOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CarOutlined,
  ExperimentOutlined,
  UploadOutlined,
  FileDoneOutlined,
  InfoCircleOutlined,
  CodeSandboxOutlined,
} from "@ant-design/icons";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, actions, sliceKey } from "./slice";
import { orderCountSaga } from "./saga";
import {
  selectOrderCountData,
  selectOrderCountLoading,
  selectOrderCountError,
} from "./selectors";

const { Title, Text } = Typography;

const LiveOrderFunnelTracker = ({ filters }) => {
  const dispatch = useDispatch();

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: orderCountSaga });

  const data = useSelector(selectOrderCountData);
  const loading = useSelector(selectOrderCountLoading);
  const error = useSelector(selectOrderCountError);

  const filtersString = JSON.stringify(filters);
  useEffect(() => {
    dispatch(
      actions.fetchOrderCount({
        filtersString,
        onSuccess: () => {},
        onFailure: (msg) => message.error(`Failed to load data: ${msg}`),
      })
    );
  }, [dispatch, filtersString]);

  const funnelItems = [
    {
      label: "Pending Orders",
      key: "pending",
      icon: <CodeSandboxOutlined />,
      color: "#6B7280",
      tooltip: "Orders created, awaiting initiation.",
    },
    {
      label: "Collection Initiated",
      key: "booked",
      icon: <InboxOutlined />,
      color: "#3B82F6",
      tooltip: "Orders assigned for collection.",
    },
    {
      label: "Collected\n(Under-Review)",
      key: "inReview",
      icon: <FileTextOutlined />,
      color: "#F97316",
      tooltip: "Samples collected and submitted for review.",
    },
    {
      label: "In-Transit",
      key: "inTransit",
      icon: <CarOutlined />,
      color: "#A855F7",
      tooltip: "Orders picked up by courier and in transit.",
    },
    {
      label: "Delivered to Lab",
      key: "deliveredToLab",
      icon: <FileDoneOutlined />,
      color: "#3B82F6",
      tooltip: "Orders delivered to the lab.",
    },
    {
      label: "Registered by Lab",
      key: "registeredToLab",
      icon: <ExperimentOutlined />,
      color: "#FACC15",
      tooltip: "Orders accepted by the lab for testing.",
    },
    {
      label: "Report Uploaded",
      key: "reportUploaded",
      icon: <UploadOutlined />,
      color: "#EC4899",
      tooltip: "Count of Reports Uploaded by Lab.",
    },
    {
      label: "Completed",
      key: "completed",
      icon: <CheckCircleOutlined />,
      color: "#22C55E",
      tooltip: "Report Reviewed and Marked as Completed.",
    },
  ];

  const getValue = (key) =>
    loading ? (
      <Spin size="small" />
    ) : (
      (data?.orderCounts?.[key] || 0).toLocaleString()
    );

  return (
    <Card
      title={
        <span style={{ color: "#fff", fontWeight: 600 }}>
          Live Order Funnel Tracker
        </span>
      }
      headStyle={{
        backgroundColor: "#0D9488",
        textAlign: "center",
        fontSize: 18,
      }}
      style={{ marginTop: 16 }}
      bodyStyle={{ padding: 24 }}
    >
      {error && <Text type="danger">Failed to load data: {error}</Text>}

      {/* Total Orders Box */}
      <Card
        style={{
          textAlign: "center",
          backgroundColor: "#ECFDF5",
          marginBottom: 32,
          border: "1px solid #D1FAE5",
        }}
        bordered={true}
      >
        <Title
          level={1}
          style={{ margin: 0, color: "#0D9488", fontSize: 36, fontWeight: 600 }}
        >
          {loading ? (
            <Spin />
          ) : (
            (data?.orderCounts?.total || 0).toLocaleString()
          )}
          <Tooltip title="Sum of all orders in different stages">
            <InfoCircleOutlined
              style={{
                marginLeft: 8,
                fontSize: 16,
                color: "#0D9488",
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Title>
        <Text strong style={{ fontSize: 16, color: "#0D9488" }}>
          Total Orders
        </Text>
      </Card>

      {/* Funnel Tracker */}
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
            gap: "4px",
          }}
        >
          {funnelItems.map((item, idx) => (
            <React.Fragment key={idx}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                  minWidth: "90px",
                }}
              >
                <Tooltip title={item.tooltip}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 20,
                      marginBottom: 6,
                      cursor: "pointer",
                    }}
                  >
                    {item.icon}
                  </div>
                </Tooltip>
                <Title
                  level={5}
                  style={{
                    marginBottom: 2,
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "20px",
                  }}
                >
                  {item.label === "Report Uploaded"
                    ? (+String(getValue("reportUploaded")).replace(/,/g, "") ||
                        0) +
                      (+String(getValue("underDispute")).replace(/,/g, "") || 0)
                    : getValue(item.key)}
                </Title>
                <Text
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#6B7280",
                    fontWeight: 500,
                    lineHeight: "12px",
                    maxWidth: "100%",
                    whiteSpace: "pre-line",
                  }}
                >
                  {item.label}
                </Text>
              </div>
              {idx < funnelItems.length - 1 && (
                <div
                  style={{
                    color: "#D1D5DB",
                    fontSize: 14,
                    alignSelf: "flex-start",
                    marginTop: "15px",
                    minWidth: "12px",
                    textAlign: "center",
                  }}
                >
                  →
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Metrics Cards */}
      <Row gutter={24} style={{ marginTop: 40 }}>
        <Col span={8}>
          <Card
            style={{
              backgroundColor: "#ECFDF5",
              textAlign: "center",
              border: "1px solid #D1FAE5",
            }}
            bordered={true}
          >
            <Text strong style={{ color: "#16A34A", fontSize: 14 }}>
              Overall Completion Rate{" "}
              <Tooltip title="Percentage of valid orders for which lab report has been approved.">
                <InfoCircleOutlined style={{ color: "#16A34A" }} />
              </Tooltip>
            </Text>
            <Title
              level={2}
              style={{ color: "#16A34A", margin: "8px 0 0 0", fontWeight: 700 }}
            >
              {loading ? (
                <Spin size="small" />
              ) : (
                `${(data?.completionRate || 0)}%`
              )}
            </Title>
          </Card>
        </Col>

        <Col span={8}>
          <Card
            style={{
              backgroundColor: "#FAF5FF",
              textAlign: "center",
              border: "1px solid #E9D5FF",
            }}
            bordered={true}
          >
            <Text strong style={{ color: "#7C3AED", fontSize: 14 }}>
              Sampling Rate{" "}
              <Tooltip title="Percentage of valid orders that reached sampling completion.">
                <InfoCircleOutlined style={{ color: "#7C3AED" }} />
              </Tooltip>
            </Text>
            <Title
              level={2}
              style={{ color: "#7C3AED", margin: "8px 0 0 0", fontWeight: 700 }}
            >
              {loading ? (
                <Spin size="small" />
              ) : (
                `${(data?.samplingRate || 0)}%`
              )}
            </Title>
          </Card>
        </Col>

        <Col span={8}>
          <Card
            style={{
              backgroundColor: "#EFF6FF",
              textAlign: "center",
              border: "1px solid #DBEAFE",
            }}
            bordered={true}
          >
            <Text strong style={{ color: "#2563EB", fontSize: 14 }}>
              Avg Processing Time{" "}
              <Tooltip title="Avg time taken from booking to report upload.">
                <InfoCircleOutlined style={{ color: "#2563EB" }} />
              </Tooltip>
            </Text>
            <Title
              level={2}
              style={{ color: "#2563EB", margin: "8px 0 0 0", fontWeight: 700 }}
            >
              {loading ? (
                <Spin size="small" />
              ) : (
                `${(data?.avgProcessingTime || 0)} days`
              )}
            </Title>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default LiveOrderFunnelTracker;
